"use client";

import { motion, AnimatePresence } from "framer-motion";
import {
  Settings,
  PanelLeftClose,
  PanelLeftOpen,
  Network,
  Plus,
  Search,
  Hash,
  Sun,
  Moon,
  Columns,
  FolderOpen,
  CheckSquare,
  Users,
  Bell,
  FileText,
  Shield,
  Calendar,
  Menu,
  HelpCircle,
  Sparkles,
  Activity,
  FileSignature,
} from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { FileExplorer } from "@/features/explorer/FileExplorer";
import { CommandPalette } from "@/components/CommandPalette";
import { HelpDrawer } from "@/components/help/HelpDrawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SidebarNavItem } from "@/components/sidebar";
import { OnboardingProgress } from "@/components/onboarding/OnboardingProgress";
import { OnboardingTour } from "@/components/onboarding/OnboardingTour";
import { WelcomeModal } from "@/components/onboarding/WelcomeModal";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { useThemeToggleShortcut } from "@/hooks/use-theme-toggle-shortcut";
import { useWorkspace } from "@/lib/workspace-context";
import { useTasksStore } from "@/stores/tasks-store";
import { useMeetingsStore } from "@/stores/meetings-store";
import { apiRequest } from "@/lib/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function WorkspaceLayout({ children }: { children: React.ReactNode }) {
  const {
    isSplit,
    setIsSplit,
    createFile,
    openFile,
    currentViewMode,
    setCurrentViewMode,
  } = useWorkspace();
  const tasksStore = useTasksStore();
  const meetingsStore = useMeetingsStore();
  const fetchTasksSummary = useTasksStore((s) => s.fetchSummary);
  const fetchMeetingsSummary = useMeetingsStore((s) => s.fetchSummary);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const stored = localStorage.getItem("wiki-sidebar-open");
    return stored === null ? true : stored === "1";
  });
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const pathname = usePathname();
  useThemeToggleShortcut();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isForm = e.target instanceof HTMLElement && (
        e.target.tagName === "INPUT" ||
        e.target.tagName === "TEXTAREA" ||
        e.target.isContentEditable
      );
      if (isForm) return;
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        setHelpOpen(prev => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    setMounted(true);
    const stored = localStorage.getItem("wiki_theme");
    const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
    const isDark = stored === "dark" || (stored === null && prefersDark);
    setIsDarkMode(isDark);
    document.documentElement.classList.toggle("dark", isDark);
  }, []);

  // Fetch task summary for the "Tareas" badge + unread notifications count
  useEffect(() => {
    if (!mounted) return;
    fetchTasksSummary();
    fetchMeetingsSummary();
    (async () => {
      try {
        const data = await apiRequest<{ items: { read_at: string | null }[] }>(
          "/v1/notifications?limit=100"
        );
        const list = data?.items || (Array.isArray(data) ? data : []);
        setUnreadNotifs(list.filter((n: any) => !n.read_at).length);
      } catch {
        // silent
      }
    })();
  }, [mounted, fetchTasksSummary, fetchMeetingsSummary]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 768) return;
    setIsSidebarOpen(false);
  }, [pathname]);

  // Persist sidebar state
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem("wiki-sidebar-open", isSidebarOpen ? "1" : "0");
  }, [isSidebarOpen]);

  const toggleTheme = () => {
    const newDark = !isDarkMode;
    setIsDarkMode(newDark);
    document.documentElement.classList.toggle("dark", newDark);
    localStorage.setItem("wiki_theme", newDark ? "dark" : "light");
  };

  const handleCreateNew = () => {
    const newId = createFile("Nota sin título");
    openFile(newId);
  };

  const openCommandPalette = () => {
    document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));
  };

  const isRootWorkspace = pathname === "/workspace";

  if (!mounted) {
    return <div className="h-screen bg-background" />; // Prevent hydration mismatch
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex h-screen overflow-hidden bg-background text-foreground transition-colors duration-300 selection:bg-primary/35 selection:text-white">
        <CommandPalette />
        <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
        <OnboardingTour />
        <WelcomeModal />
        
        {/* Ribbon — minimal left bar (Obsidian-style) */}
        <nav className="w-12 flex flex-col items-center py-3 bg-secondary/30 border-r border-border/20 z-30 shrink-0">
          {/* Logo -> click goes to Inicio */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setCurrentViewMode("dashboard")}
                className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center mb-4 shadow-sm shadow-primary/15 hover:opacity-90 active:scale-[0.96] transition-all"
                aria-label="Ir a Inicio"
              >
                <span className="font-bold text-sm tracking-tight">A</span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Inicio — Arsenal Wiki</TooltipContent>
          </Tooltip>
          
          <div className="flex flex-col gap-1.5 flex-1 items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setCurrentViewMode("dashboard")}
                  className={cn(
                    "p-2 rounded-md transition-all flex items-center justify-center active:scale-[0.95]",
                    isRootWorkspace && currentViewMode === "dashboard"
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  )}
                  aria-label="Inicio"
                >
                  <Sparkles className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Inicio (Dashboard)</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setCurrentViewMode("normal")}
                  className={cn(
                    "p-2 rounded-md transition-all flex items-center justify-center active:scale-[0.95]",
                    isRootWorkspace && currentViewMode === "normal"
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  )}
                  aria-label="Editor de notas"
                >
                  <FileText className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Editor de notas</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setCurrentViewMode("web")}
                  className={cn(
                    "p-2 rounded-md transition-all flex items-center justify-center active:scale-[0.95]",
                    isRootWorkspace && currentViewMode === "web"
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  )}
                  aria-label="Grafo de conocimiento"
                  data-tour="graph-button"
                >
                  <Network className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Vista de grafo</TooltipContent>
            </Tooltip>

            <div className="w-5 h-px bg-border/40 my-1" />

            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={handleCreateNew}
                  className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 active:scale-[0.95] transition-all flex items-center justify-center"
                  aria-label="Nueva nota"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Nueva nota</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={openCommandPalette}
                  className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 active:scale-[0.95] transition-all flex items-center justify-center"
                  aria-label="Buscar"
                  data-tour="command-palette"
                >
                  <Search className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Buscar (⌘K)</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={() => {
                    setCurrentViewMode("normal");
                    setIsSplit(!isSplit);
                  }}
                  className={cn(
                    "p-2 rounded-md transition-all flex items-center justify-center active:scale-[0.95]",
                    isSplit && isRootWorkspace && currentViewMode === "normal"
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  )}
                  aria-label="Dividir pantalla"
                >
                  <Columns className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Dividir pantalla</TooltipContent>
            </Tooltip>
          </div>

          <div className="flex flex-col gap-1.5 mt-auto items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={() => setHelpOpen(true)} 
                  className={cn(
                    "p-2 rounded-md transition-all flex items-center justify-center active:scale-[0.95]",
                    helpOpen ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  )}
                  aria-label="Ayuda"
                >
                  <HelpCircle className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Centro de Ayuda (?)</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={toggleTheme} 
                  className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/60 active:scale-[0.95] transition-all flex items-center justify-center"
                  aria-label="Cambiar tema"
                >
                  {isDarkMode ? <Sun className="w-4 h-4" strokeWidth={1.75} /> : <Moon className="w-4 h-4" strokeWidth={1.75} />}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{isDarkMode ? "Modo claro" : "Modo oscuro"}</TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Link 
                  href="/workspace/settings"
                  className={cn(
                    "p-2 rounded-md transition-all flex items-center justify-center active:scale-[0.95]",
                    pathname === "/workspace/settings" ? "text-primary bg-primary/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  )}
                  aria-label="Configuración"
                >
                  <Settings className="w-4 h-4" strokeWidth={1.75} />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">Configuración</TooltipContent>
            </Tooltip>
          </div>
        </nav>

        {/* Mobile backdrop */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.button
              type="button"
              aria-label="Cerrar barra lateral"
              onClick={() => setIsSidebarOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 z-30 bg-black/40 backdrop-blur-sm cursor-default"
            />
          )}
        </AnimatePresence>

        {/* Sidebar - Explorer + Navigation */}
        <motion.aside
          initial={false}
          animate={{ width: isSidebarOpen ? 240 : 0, opacity: isSidebarOpen ? 1 : 0 }}
          className={cn(
            "relative flex flex-col bg-secondary/10 border-r border-border/20 h-full overflow-hidden select-none z-40 shrink-0",
            "md:relative md:z-20",
            "fixed md:static inset-y-0 left-0",
            !isSidebarOpen && "border-none"
          )}
          aria-label="Navegación principal"
          data-tour="file-explorer"
        >
          {/* Sidebar Header */}
          <div className="flex items-center justify-between px-4 h-11 border-b border-border/15 shrink-0">
            <span className="text-[11px] font-bold tracking-tight text-foreground/80 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-primary/80" />
              Arsenal Wiki
            </span>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-1 rounded-md hover:bg-secondary/60 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Cerrar barra lateral"
              title="Ocultar barra lateral"
            >
              <PanelLeftClose className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </div>

          <ScrollArea className="flex-1 min-h-0">
            {/* Primary Workspace Views */}
            <div className="px-3 pt-2.5 pb-2 border-b border-border/15">
              <nav className="space-y-0.5" aria-label="Vistas principales">
                <SidebarNavItem
                  icon={<Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Inicio"
                  active={isRootWorkspace && currentViewMode === "dashboard"}
                  onClick={() => setCurrentViewMode("dashboard")}
                />
                <SidebarNavItem
                  icon={<FileText className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Editor de notas"
                  active={isRootWorkspace && currentViewMode === "normal"}
                  onClick={() => setCurrentViewMode("normal")}
                />
                <SidebarNavItem
                  icon={<Network className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Grafo de conocimiento"
                  active={isRootWorkspace && currentViewMode === "web"}
                  onClick={() => setCurrentViewMode("web")}
                />
              </nav>
            </div>

            {/* File Explorer */}
            <div className="pb-2">
              <FileExplorer />
            </div>

            <OnboardingProgress />

            {/* Navigation menu — management pages */}
            <div className="px-3 pt-3 pb-3 border-t border-border/20 mt-1">
              <span className="px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 block mb-1.5">
                Gestión y Equipo
              </span>
              <nav className="space-y-0.5" aria-label="Navegación de gestión">
                <SidebarNavItem
                  icon={<FolderOpen className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Proyectos"
                  href="/workspace/spaces"
                  active={pathname === "/workspace/spaces" || pathname.startsWith("/workspace/spaces/")}
                />
                <SidebarNavItem
                  icon={<CheckSquare className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Tareas"
                  href="/workspace/tasks"
                  active={pathname === "/workspace/tasks"}
                  badge={tasksStore.summary?.todo || undefined}
                />
                <SidebarNavItem
                  icon={<Calendar className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Reuniones"
                  href="/workspace/meetings"
                  active={pathname === "/workspace/meetings" || pathname.startsWith("/workspace/meetings/")}
                  badge={meetingsStore.summary?.pending_rsvp_count || undefined}
                />
                <SidebarNavItem
                  icon={<Users className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Miembros"
                  href="/workspace/users"
                  active={pathname === "/workspace/users"}
                />
                <SidebarNavItem
                  icon={<Bell className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Notificaciones"
                  href="/workspace/notifications"
                  active={pathname === "/workspace/notifications"}
                  badge={unreadNotifs || undefined}
                />
                <SidebarNavItem
                  icon={<Search className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Búsqueda avanzada"
                  href="/workspace/search"
                  active={pathname === "/workspace/search"}
                />
                <SidebarNavItem
                  icon={<FileText className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Plantillas"
                  href="/workspace/templates"
                  active={pathname === "/workspace/templates"}
                />
                <SidebarNavItem
                  icon={<FileSignature className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Aprobaciones"
                  href="/workspace/workflows"
                  active={pathname === "/workspace/workflows"}
                />
                <SidebarNavItem
                  icon={<Activity className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Salud del Wiki"
                  href="/workspace/health"
                  active={pathname === "/workspace/health"}
                />
                <SidebarNavItem
                  icon={<Shield className="w-3.5 h-3.5" strokeWidth={1.75} />}
                  label="Administración"
                  href="/workspace/admin"
                  active={pathname === "/workspace/admin"}
                />
              </nav>
            </div>
          </ScrollArea>
        </motion.aside>

        {/* Main Area */}
        <main id="main-content" className="flex-1 flex flex-col min-w-0 overflow-hidden relative bg-background">
          {/* Unified Compact Global Header (h-11) */}
          <header className="h-11 border-b border-border/30 px-4 flex items-center justify-between gap-3 bg-background/80 backdrop-blur-sm select-none shrink-0 z-30">
            {/* Left: Sidebar toggle + Breadcrumbs / Context */}
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors shrink-0"
                aria-label={isSidebarOpen ? "Ocultar barra lateral" : "Mostrar barra lateral"}
                title={isSidebarOpen ? "Ocultar barra lateral" : "Mostrar barra lateral"}
              >
                <Menu className="w-4 h-4 md:hidden" aria-hidden="true" />
                {isSidebarOpen ? (
                  <PanelLeftClose className="w-4 h-4 hidden md:block" aria-hidden="true" />
                ) : (
                  <PanelLeftOpen className="w-4 h-4 hidden md:block" aria-hidden="true" />
                )}
              </button>

              {isRootWorkspace ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground truncate">
                  <span className="font-medium text-foreground/85">Workspace</span>
                  <span className="text-muted-foreground/40">/</span>
                  <span className="text-foreground font-medium">
                    {currentViewMode === "dashboard"
                      ? "Inicio"
                      : currentViewMode === "web"
                        ? "Grafo de conocimiento"
                        : "Editor"}
                  </span>
                </div>
              ) : (
                <Breadcrumbs />
              )}
            </div>

            {/* Center: Quick Command / Search Bar */}
            <button
              type="button"
              onClick={openCommandPalette}
              className="hidden sm:flex items-center justify-between gap-3 h-7 px-3 w-full max-w-xs rounded-lg bg-secondary/40 hover:bg-secondary/70 border border-border/40 text-xs text-muted-foreground hover:text-foreground transition-all"
              title="Buscar notas o ejecutar comandos (⌘K)"
            >
              <span className="flex items-center gap-2 truncate">
                <Search className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0" />
                <span className="truncate">Buscar notas o comandos...</span>
              </span>
              <kbd className="px-1.5 py-0.5 rounded bg-background/80 border border-border/50 text-[10px] font-mono text-muted-foreground shrink-0">
                ⌘K
              </kbd>
            </button>

            {/* Right: Global View Switcher (Inicio | Grafo | Editor) */}
            <div className="flex items-center bg-secondary/50 rounded-lg p-0.5 border border-border/40 shrink-0">
              <button
                onClick={() => setCurrentViewMode("dashboard")}
                className={cn(
                  "px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1.5",
                  isRootWorkspace && currentViewMode === "dashboard"
                    ? "bg-background text-primary shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Inicio</span>
              </button>
              <button
                onClick={() => setCurrentViewMode("normal")}
                className={cn(
                  "px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1.5",
                  isRootWorkspace && currentViewMode === "normal"
                    ? "bg-background text-primary shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <FileText className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Editor</span>
              </button>
              <button
                onClick={() => setCurrentViewMode("web")}
                className={cn(
                  "px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1.5",
                  isRootWorkspace && currentViewMode === "web"
                    ? "bg-background text-primary shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Network className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Grafo</span>
              </button>
            </div>
          </header>

          {/* Workspace content page fills this container */}
          <div className="flex-1 flex min-w-0 overflow-hidden relative">
            {children}
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
